/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { Template } from "../utils/template.js";
import { isLLMContent, isLLMContentArray, isTextCapabilityPart, } from "../data/common.js";
import { bubbleUpInputsIfNeeded } from "../bubble.js";
export { ParameterManager };
class ParameterManager {
    graph;
    inputs;
    constructor(graph, inputs) {
        this.graph = graph;
        this.inputs = inputs;
    }
    propertiesSchema() {
        const params = this.#scanGraph();
        return Object.fromEntries(params.map((param) => {
            const id = param.path;
            return [
                id,
                {
                    type: "object",
                    behavior: ["llm-content"],
                    description: param.title,
                },
            ];
        }));
    }
    #scanGraph() {
        const knownParams = new Set(Object.keys(this.inputs || {}));
        return this.graph.nodes
            .map(({ configuration }) => {
            if (!configuration)
                return null;
            return this.#scanConfiguration(knownParams, configuration);
        })
            .filter((item) => item !== null)
            .flat();
    }
    #scanConfiguration(knownParams, configuration) {
        const params = [];
        // Scan for all LLMContent/LLMContent[] properties
        Object.values(configuration).forEach((value) => {
            let content = null;
            if (isLLMContent(value)) {
                content = [value];
            }
            else if (isLLMContentArray(value)) {
                content = value;
            }
            const last = content?.at(-1);
            if (!last)
                return;
            last.parts.forEach((part) => {
                if (isTextCapabilityPart(part)) {
                    const template = new Template(part.text);
                    template.placeholders.forEach((placeholder) => {
                        if (placeholder.type === "param" &&
                            !knownParams.has(placeholder.path)) {
                            params.push(placeholder);
                            knownParams.add(placeholder.path);
                        }
                    });
                }
            });
        });
        return params;
    }
    async requestParameters(context, path, result) {
        const { inputs, graph } = this;
        const { descriptor } = result;
        const { configuration } = descriptor;
        if (!configuration)
            return context;
        const knownInputs = new Set(Object.keys(this.inputs || {}));
        const params = this.#scanConfiguration(knownInputs, configuration);
        const parameterInputs = [];
        if (params.length > 0) {
            console.table(params);
            // Simulate a subgraph that consists of 1+ input nodes.
            await context.probe?.report?.({
                type: "graphstart",
                data: {
                    graph: virtualGraph(),
                    graphId: "",
                    path: [...path, -1],
                    timestamp: timestamp(),
                },
            });
            // TODO: Implement support for multiple inputs at once.
            for (const [idx, param] of params.entries()) {
                const metadata = graph.metadata?.parameters?.[param.path];
                const format = metadata?.modality?.join(",") || "text";
                const firstSample = metadata?.sample?.at(0);
                const sample = firstSample
                    ? { examples: [JSON.stringify(firstSample)] }
                    : {};
                const schema = {
                    type: "object",
                    properties: {
                        [param.path]: {
                            type: "object",
                            title: metadata?.title || param.title,
                            description: metadata?.description || param.title,
                            behavior: ["llm-content"],
                            ...sample,
                            format,
                        },
                    },
                };
                const paramDescriptor = {
                    id: crypto.randomUUID(),
                    type: "input",
                    configuration: { schema },
                };
                await context.probe?.report?.({
                    type: "nodestart",
                    data: {
                        node: paramDescriptor,
                        inputs: { schema },
                        path: [...path, -1, idx],
                        timestamp: timestamp(),
                    },
                });
                const paramsResult = { ...result, inputs: { schema } };
                await bubbleUpInputsIfNeeded(graph, context, paramDescriptor, paramsResult, [...path, -1, idx]);
                if (paramsResult.outputs) {
                    parameterInputs.push(...Object.entries(paramsResult.outputs).map(([id, content]) => {
                        const path = `/env/parameters/${id}`;
                        return { path, data: [content] };
                    }));
                }
                await context.probe?.report?.({
                    type: "nodeend",
                    data: {
                        node: paramDescriptor,
                        inputs: { schema },
                        outputs: paramsResult.outputs || {},
                        path: [...path, -1, idx],
                        timestamp: timestamp(),
                        newOpportunities: [],
                    },
                });
            }
            await context.probe?.report?.({
                type: "graphend",
                data: {
                    path: [...path, -1],
                    timestamp: timestamp(),
                },
            });
        }
        return {
            ...context,
            fileSystem: context.fileSystem?.createModuleFileSystem({
                graphUrl: graph.url,
                env: updateEnv(parameterInputs, context.fileSystem, inputs),
            }),
        };
    }
}
function updateEnv(params, fileSystem, inputs) {
    const currentEnv = fileSystem?.env() || [];
    const newEnv = inputs
        ? Object.entries(inputs).map(([path, input]) => {
            let data;
            if (typeof input === "string") {
                data = [{ parts: [{ text: input }] }];
            }
            else if (isLLMContent(input)) {
                data = [input];
            }
            else {
                data = input;
            }
            return { path: `/env/parameters/${path}`, data };
        })
        : [];
    return [...currentEnv, ...newEnv, ...params];
}
function virtualGraph() {
    return {
        nodes: [],
        edges: [],
        virtual: true,
    };
}
function timestamp() {
    return globalThis.performance.now();
}
//# sourceMappingURL=parameter-manager.js.map